import { types, applySnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import { toJS } from 'mobx';
import * as constants from '../../../common/constants';
import utils from '../../../common/utils';
import { buildRowScopedExportPayload } from '../common/utils/auditExportColumnUtils';

const ClusterListData = types.model('ClusterListData', {
  clusterName: types.maybeNull(types.string),
  serverDateTime: types.maybeNull(types.string),
  createdDate: types.maybeNull(types.number),
  clusterId: types.maybeNull(types.string),
  identifierId: types.maybeNull(types.string),
  userCommand: types.maybeNull(types.string),
  userId: types.maybeNull(types.string),
  dateStamp: types.maybeNull(types.string),
  requestId: types.maybeNull(types.string),
  type: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  fromAgentic: types.maybeNull(types.boolean)
});

const ClusterList = types
  .model('ClusterList', {
    data: types.optional(types.array(ClusterListData), []),
    totalCount: types.optional(types.number, 0),
    totalPages: types.optional(types.number, 0),
    currentPage: types.optional(types.number, 0),
    pageSize: types.optional(types.number, constants.PAGINATION.LIMIT),
    lastPayload: types.optional(types.frozen(), {})
  })
  .views((self) => ({
    get clusterData() {
      return toJS(self.data);
    },
    get hasData() {
      return self.data.length > 0;
    },
    get paginationInfo() {
      return {
        totalCount: self.totalCount,
        totalPages: self.totalPages,
        currentPage: self.currentPage,
        pageSize: self.pageSize
      };
    },
    /** Export filters; start/end dates come from loaded row timestamps, not search date pickers. */
    get exportPayload() {
      return buildRowScopedExportPayload(self.lastPayload, self.data);
    }
  }))
  .actions((self) => ({
    setPageSize(size) {
      // Avoid directly mutating `self.pageSize`
      applySnapshot(self, { ...self, pageSize: size });
    },
    setLastPayload(payload) {
      // Avoid directly mutating `self.lastPayload`
      applySnapshot(self, { ...self, lastPayload: payload });
    },
    setTotalCount(count) {
      // Avoid directly mutating `self.totalCount`
      applySnapshot(self, { ...self, totalCount: count });
    },
    setTotalPages(pages) {
      // Avoid directly mutating `self.totalPages`
      applySnapshot(self, { ...self, totalPages: pages });
    },
    setCurrentPage(page) {
      // Avoid directly mutating `self.currentPage`
      applySnapshot(self, { ...self, currentPage: page });
    },
    fetchInitial(payload = {}) {
      self.resetData();

      const requestPayload = {
        ...payload,
        offset: 0,
        limit: self.pageSize
      };

      self.setLastPayload(requestPayload);

      Framework.getEventManager().publish(constants.GET_CLUSTER_LISTING_DATA, {
        payload: requestPayload
      });
    },
    fetchPage(page, limit = null) {
      // Fetch a specific page
      const pageSize = limit || self.pageSize;
      const offset = page * pageSize;

      const requestPayload = {
        ...self.lastPayload,
        offset,
        limit: pageSize
      };

      self.setPageSize(pageSize);
      self.setLastPayload(requestPayload);

      Framework.getEventManager().publish(constants.GET_CLUSTER_LISTING_DATA, {
        payload: requestPayload
      });
    },
    fetch(payload, tenantId) {
      if (payload?.isInitialState) {
        const initialState = [];
        applySnapshot(self.data, initialState);
        Framework.getEventManager().publish(constants.SET_CLUSTER_LISTING_DATA);
      }
      Framework.getEventManager().publish(constants.GET_CLUSTER_LISTING_DATA, {
        payload,
        tenantId
      });
    },
    setResponse(response) {
      if (!response) {
        Framework.getEventManager().publish(constants.SET_CLUSTER_LISTING_DATA);
        return;
      }
      let responseData = [];
      let paginationData = null;

      if (response.status === 'success' && Array.isArray(response.data)) {
        responseData = response.data;
        paginationData = response.pagination || null;
      } else if (response.success !== undefined) {
        responseData = response.data || [];
        paginationData = response.pagination || null;
      } else if (Array.isArray(response)) {
        responseData = response;
      } else if (response.data) {
        responseData = Array.isArray(response.data.data) ? response.data.data : [];
        paginationData = response.pagination || null;
      }

      const enrichedData = responseData.map((item) => ({
        ...item,
        createdDate:
          utils.toEpochUTC(item.dateStamp ?? item.serverDateTime) ??
          (typeof item.dateStamp === 'number' ? item.dateStamp : null)
      }));

      applySnapshot(self.data, enrichedData);

      if (paginationData) {
        self.setTotalCount(paginationData.totalCount || 0);
        self.setTotalPages(paginationData.totalPages || 0);

        const currentPageIndex =
          paginationData.offset !== undefined && paginationData.limit
            ? Math.floor(paginationData.offset / paginationData.limit)
            : paginationData.currentPage || 0;
        self.setCurrentPage(currentPageIndex);
      } else {
        self.setTotalCount(responseData.length);
        self.setTotalPages(1);
        self.setCurrentPage(0);
      }
      Framework.getEventManager().publish(constants.SET_CLUSTER_LISTING_DATA);
    },
    resetData() {
      applySnapshot(self.data, []);
      self.setTotalCount(0);
      self.setTotalPages(0);
      self.setCurrentPage(0);
      self.setLastPayload({});
    },
    resetStoreValues() {
      self.resetData();
      Framework.getEventManager().publish(constants.SET_CLUSTER_LISTING_DATA);
    }
  }));

export default ClusterList;
